import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import {
  Route,
  Play,
  Timer,
  Palette,
  Gauge,
  Plus,
  Trash2,
  Lock,
  Crown,
  Volume2,
  RotateCcw,
  Sun,
  Moon,
} from 'lucide-react';

const TABS = [
  { id: 'trail', label: 'Trip Trail', icon: Route },
  { id: 'animation', label: 'Animation', icon: Play },
  { id: 'stopTimer', label: 'Stop Timer', icon: Timer },
  { id: 'cardCustom', label: 'Device Cards', icon: Palette },
  { id: 'speedAlert', label: 'Road Speed Alert', icon: Gauge },
];

const DEFAULT_SETTINGS = {
  trail: {
    enabled: false,
    trailLength: 20,
    showAllDevices: false,
    useRoadSnap: true,
    colorMode: 'status',
    singleColor: '#2563eb',
    opacity: 0.6,
    thickness: 3,
    showDataPoints: false,
    dataPointColor: '',
    dataPointSize: 5,
    showSnappedPoints: false,
    snappedPointColor: '#06b6d4',
    snappedPointSize: 5,
  },
  animation: { enabled: false, smoothRotation: true, useRoadSnap: true },
  stopTimer: {
    enabled: false,
    requireIgnitionOn: true,
    thresholds: [
      { minMin: 0, maxMin: 5, color: '#eab308' },
      { minMin: 5, maxMin: 30, color: '#f97316' },
      { minMin: 30, maxMin: null, color: '#dc2626' },
    ],
  },
  cardCustom: {
    enabled: false,
    lightModeEnabled: true,
    darkModeEnabled: true,
    offlineColor: '',
    stoppedIgnOffColor: '#e1ffe6',
    stoppedIgnOnColor: '#ffffdc',
    movingColor: '#a0ffc8',
    speedAlertColor: '#ff3c4b',
    ignOnConditionEnabled: false,
    ignOnConditionMinutes: 5,
    darkModeCustom: false,
    darkOfflineColor: '',
    darkStoppedIgnOffColor: '#264f34',
    darkStoppedIgnOnColor: '#4f4822',
    darkMovingColor: '#1e5236',
    darkSpeedAlertColor: '#5e2230',
    borderEnabled: false,
    borderThickness: 4,
    lightBorderEnabled: true,
    darkBorderEnabled: true,
    borderOfflineColor: '',
    borderStoppedIgnOffColor: '#a0ffbe',
    borderStoppedIgnOnColor: '#ffff82',
    borderMovingColor: '#34d399',
    borderSpeedAlertColor: '#ef4444',
    darkBorderCustom: false,
    darkBorderOfflineColor: '',
    darkBorderStoppedIgnOffColor: '#a0ffbe',
    darkBorderStoppedIgnOnColor: '#ffff82',
    darkBorderMovingColor: '#34d399',
    darkBorderSpeedAlertColor: '#f87171',
  },
  speedAlert: {
    enabled: false,
    tiers: [
      { minSpeed: 40, maxSpeed: 60, bufferType: 'percent', bufferValue: 10 },
      { minSpeed: 61, maxSpeed: 99, bufferType: 'percent', bufferValue: 7 },
      { minSpeed: 100, maxSpeed: null, bufferType: 'kmh', bufferValue: 15 },
    ],
    alertTone: 'default',
    alertDuration: 'once',
    showSpeedLimit: true,
    showDismissButton: true,
  },
};

const CARD_DEFAULTS = {
  offlineColor: '',
  stoppedIgnOffColor: '#e1ffe6',
  stoppedIgnOnColor: '#ffffdc',
  movingColor: '#a0ffc8',
  speedAlertColor: '#ff3c4b',
};

const DARK_CARD_DEFAULTS = {
  darkOfflineColor: '',
  darkStoppedIgnOffColor: '#264f34',
  darkStoppedIgnOnColor: '#4f4822',
  darkMovingColor: '#1e5236',
  darkSpeedAlertColor: '#5e2230',
};

const BORDER_DEFAULTS = {
  borderOfflineColor: '',
  borderStoppedIgnOffColor: '#a0ffbe',
  borderStoppedIgnOnColor: '#ffff82',
  borderMovingColor: '#34d399',
  borderSpeedAlertColor: '#ef4444',
};

const DARK_BORDER_DEFAULTS = {
  darkBorderOfflineColor: '',
  darkBorderStoppedIgnOffColor: '#a0ffbe',
  darkBorderStoppedIgnOnColor: '#ffff82',
  darkBorderMovingColor: '#34d399',
  darkBorderSpeedAlertColor: '#f87171',
};

const ALERT_TONES = [
  { id: 'default', label: 'Default Beep', freq: 800, duration: 200 },
  { id: 'alarm', label: 'Alarm', freq: 1200, duration: 400 },
  { id: 'chime', label: 'Chime', freq: 600, duration: 150 },
  { id: 'urgent_siren', label: 'Urgent Siren', freq: 900, duration: 600 },
  { id: 'triple_beep', label: 'Triple Beep', freq: 1000, duration: 150 },
  { id: 'continuous', label: 'Continuous Alarm', freq: 1100, duration: 300 },
  { id: 'low_warning', label: 'Low Warning', freq: 350, duration: 500 },
  { id: 'none', label: 'No Sound', freq: 0, duration: 0 },
];

const ALERT_DURATIONS = [
  { id: 'once', label: 'Once' },
  { id: '3s', label: '3 seconds' },
  { id: '5s', label: '5 seconds' },
  { id: '10s', label: '10 seconds' },
  { id: 'until_dismissed', label: 'Until dismissed' },
];

// Play a test tone using Web Audio API — returns AudioContext for stopping
const playTestTone = (toneId, durationId = 'once') => {
  const tone = ALERT_TONES.find(t => t.id === toneId);
  if (!tone || tone.freq === 0) return null;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    const playOnce = (startTime) => {
      if (toneId === 'urgent_siren') {
        // Alternating high/low siren
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sawtooth';
        gain.gain.value = 0.3;
        osc.frequency.setValueAtTime(900, startTime);
        osc.frequency.linearRampToValueAtTime(1400, startTime + 0.15);
        osc.frequency.linearRampToValueAtTime(900, startTime + 0.3);
        osc.frequency.linearRampToValueAtTime(1400, startTime + 0.45);
        osc.frequency.linearRampToValueAtTime(900, startTime + 0.6);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.6);
        osc.start(startTime);
        osc.stop(startTime + 0.65);
        return 0.7;
      } else if (toneId === 'triple_beep') {
        // 3 rapid beeps
        for (let i = 0; i < 3; i++) {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g);
          g.connect(ctx.destination);
          o.frequency.value = tone.freq;
          o.type = 'sine';
          g.gain.value = 0.3;
          g.gain.exponentialRampToValueAtTime(0.001, startTime + i * 0.18 + 0.12);
          o.start(startTime + i * 0.18);
          o.stop(startTime + i * 0.18 + 0.15);
        }
        return 0.6;
      } else if (toneId === 'continuous') {
        // Repeating beeps
        for (let i = 0; i < 3; i++) {
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g);
          g.connect(ctx.destination);
          o.frequency.value = tone.freq;
          o.type = 'square';
          g.gain.value = 0.2;
          g.gain.exponentialRampToValueAtTime(0.001, startTime + i * 0.4 + 0.25);
          o.start(startTime + i * 0.4);
          o.stop(startTime + i * 0.4 + 0.3);
        }
        return 1.2;
      } else if (toneId === 'low_warning') {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = tone.freq;
        osc.type = 'triangle';
        gain.gain.value = 0.3;
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.5);
        osc.start(startTime);
        osc.stop(startTime + 0.55);
        return 0.6;
      } else if (toneId === 'alarm') {
        // Double beep
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = tone.freq;
        osc.type = 'sine';
        gain.gain.value = 0.3;
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + tone.duration / 1000);
        osc.start(startTime);
        osc.stop(startTime + tone.duration / 1000 + 0.05);
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.value = tone.freq;
        osc2.type = 'sine';
        gain2.gain.value = 0.3;
        gain2.gain.exponentialRampToValueAtTime(0.001, startTime + 0.25 + tone.duration / 1000);
        osc2.start(startTime + 0.25);
        osc2.stop(startTime + 0.25 + tone.duration / 1000 + 0.05);
        return 0.7;
      } else {
        // Default / chime — single beep
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = tone.freq;
        osc.type = 'sine';
        gain.gain.value = 0.3;
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + tone.duration / 1000);
        osc.start(startTime);
        osc.stop(startTime + tone.duration / 1000 + 0.05);
        return tone.duration / 1000 + 0.1;
      }
    };

    // For test: just play once
    if (durationId === 'once') {
      playOnce(ctx.currentTime);
    } else {
      // Play the pattern for the configured duration
      const secs = durationId === '3s' ? 3 : durationId === '5s' ? 5 : durationId === '10s' ? 10 : 3;
      let t = ctx.currentTime;
      const end = t + secs;
      while (t < end) {
        const len = playOnce(t);
        t += len + 0.15;
      }
    }
    return ctx;
  } catch { return null; }
};

// Tab content: Trip Direction Trail
const TrailTab = ({ settings, onChange, allowed, snapAllowed }) => {
  if (!allowed) return <LockedMessage feature="Trip Direction Trail" />;
  const s = { ...DEFAULT_SETTINGS.trail, ...settings };
  return (
    <div className="space-y-5" data-testid="trail-tab">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">Enable Trip Trail</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Show a fading breadcrumb trail behind moving devices</p>
        </div>
        <Switch checked={s.enabled} onCheckedChange={(v) => onChange({ ...s, enabled: v })} data-testid="trail-toggle" />
      </div>
      {s.enabled && (
        <>
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Trail Length: {s.trailLength} positions</Label>
            <Slider
              value={[s.trailLength]}
              onValueChange={([v]) => onChange({ ...s, trailLength: v })}
              min={5} max={30} step={5}
              className="w-full"
              data-testid="trail-length-slider"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
              <span>5</span><span>30</span>
            </div>
          </div>

          {/* Color Mode */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Trail Color</Label>
            <div className="flex gap-2">
              <button
                onClick={() => onChange({ ...s, colorMode: 'status' })}
                className={`flex-1 text-xs py-2 px-3 rounded border transition-colors ${
                  s.colorMode === 'status' ? 'bg-[#2563eb]/10 border-[#2563eb] text-[#2563eb] font-medium' : 'border-border hover:bg-muted/50'
                }`}
                data-testid="trail-color-status"
              >
                Use Status Colors
              </button>
              <button
                onClick={() => onChange({ ...s, colorMode: 'single' })}
                className={`flex-1 text-xs py-2 px-3 rounded border transition-colors ${
                  s.colorMode === 'single' ? 'bg-[#2563eb]/10 border-[#2563eb] text-[#2563eb] font-medium' : 'border-border hover:bg-muted/50'
                }`}
                data-testid="trail-color-single"
              >
                Single Color
              </button>
            </div>
            {s.colorMode === 'single' && (
              <div className="flex items-center gap-2 mt-2">
                <div className="w-6 h-6 rounded border" style={{ backgroundColor: s.singleColor }} />
                <input
                  type="color"
                  value={s.singleColor || '#2563eb'}
                  onChange={(e) => onChange({ ...s, singleColor: e.target.value })}
                  className="w-8 h-8 rounded cursor-pointer border-0"
                />
                <span className="text-xs text-muted-foreground">{s.singleColor}</span>
              </div>
            )}
          </div>

          {/* Opacity */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Opacity: {Math.round((s.opacity ?? 0.6) * 100)}%</Label>
            <Slider
              value={[Math.round((s.opacity ?? 0.6) * 100)]}
              onValueChange={([v]) => onChange({ ...s, opacity: v / 100 })}
              min={10} max={100} step={5}
              className="w-full"
              data-testid="trail-opacity-slider"
            />
          </div>

          {/* Thickness */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Thickness: {s.thickness ?? 3}px</Label>
            <Slider
              value={[s.thickness ?? 3]}
              onValueChange={([v]) => onChange({ ...s, thickness: v })}
              min={1} max={8} step={1}
              className="w-full"
              data-testid="trail-thickness-slider"
            />
          </div>

          <div className="space-y-3 rounded-lg border bg-muted/20 p-3" data-testid="trail-road-snap-settings">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className={`text-sm ${!snapAllowed ? 'text-muted-foreground' : ''}`}>Use Snap to Road</Label>
                <p className="text-xs text-muted-foreground">Curve the trail along road geometry instead of raw GPS straight lines</p>
              </div>
              <Switch
                checked={snapAllowed && s.useRoadSnap !== false}
                onCheckedChange={(v) => onChange({ ...s, useRoadSnap: v, showSnappedPoints: v ? s.showSnappedPoints || false : false })}
                disabled={!snapAllowed}
                data-testid="trail-road-snap-toggle"
              />
            </div>
            {!snapAllowed && <PremiumFeatureNote />}

            {snapAllowed && s.useRoadSnap !== false && (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <Label className="text-sm">Show Snapped Points</Label>
                    <p className="text-xs text-muted-foreground">Display the interpolated road-snap points used to shape the trail</p>
                  </div>
                  <Switch
                    checked={s.showSnappedPoints || false}
                    onCheckedChange={(v) => onChange({ ...s, showSnappedPoints: v })}
                    data-testid="trail-snapped-points-toggle"
                  />
                </div>

                {s.showSnappedPoints && (
                  <div className="space-y-3 rounded-lg border bg-background/80 p-3" data-testid="trail-snapped-points-controls">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-2 block">Snapped Point Size: {s.snappedPointSize ?? 5}px</Label>
                      <Slider
                        value={[s.snappedPointSize ?? 5]}
                        onValueChange={([v]) => onChange({ ...s, snappedPointSize: v })}
                        min={3}
                        max={14}
                        step={1}
                        className="w-full"
                        data-testid="trail-snapped-point-size-slider"
                      />
                      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                        <span>3</span><span>14</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 rounded border p-2" data-testid="trail-snapped-point-color-row">
                      <div className="w-6 h-6 rounded-full border flex-shrink-0" style={{ backgroundColor: s.snappedPointColor || '#06b6d4' }} />
                      <span className="text-xs text-muted-foreground flex-1">{s.snappedPointColor || '#06b6d4'}</span>
                      <input
                        type="color"
                        value={s.snappedPointColor || '#06b6d4'}
                        onChange={(e) => onChange({ ...s, snappedPointColor: e.target.value })}
                        className="w-6 h-6 rounded cursor-pointer border-0"
                        data-testid="trail-snapped-point-color-input"
                      />
                      <button
                        onClick={() => onChange({ ...s, snappedPointColor: '#06b6d4' })}
                        className="text-muted-foreground hover:text-foreground"
                        title="Reset snapped point color to default (#06b6d4)"
                        data-testid="trail-snapped-point-color-reset"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* GPS Data Points */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Show GPS Data Points</Label>
              <p className="text-xs text-muted-foreground">Display dots at each GPS position on the trail</p>
            </div>
            <Switch checked={s.showDataPoints || false} onCheckedChange={(v) => onChange({ ...s, showDataPoints: v })} data-testid="trail-datapoints-toggle" />
          </div>
          {s.showDataPoints && (
            <div className="space-y-3">
              {/* Dot Size */}
              <div>
                <Label className="text-xs text-muted-foreground mb-2 block">Point Size: {s.dataPointSize ?? 5}px</Label>
                <Slider
                  value={[s.dataPointSize ?? 5]}
                  onValueChange={([v]) => onChange({ ...s, dataPointSize: v })}
                  min={3} max={14} step={1}
                  className="w-full"
                  data-testid="trail-datapoint-size-slider"
                />
                <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                  <span>3</span><span>14</span>
                </div>
              </div>

              {/* Custom Color Toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs">Custom Point Color</Label>
                  <p className="text-[10px] text-muted-foreground">When off, dots match the trail color automatically</p>
                </div>
                <Switch checked={!!s.dataPointColor} onCheckedChange={(v) => onChange({ ...s, dataPointColor: v ? '#ff4444' : '' })} data-testid="trail-datapoint-color-toggle" />
              </div>
              {s.dataPointColor && (
                <div className="flex items-center gap-3 p-2 rounded border">
                  <div className="w-6 h-6 rounded-full border flex-shrink-0" style={{ backgroundColor: s.dataPointColor }} />
                  <span className="text-xs text-muted-foreground flex-1">{s.dataPointColor}</span>
                  <input
                    type="color"
                    value={s.dataPointColor}
                    onChange={(e) => onChange({ ...s, dataPointColor: e.target.value })}
                    className="w-6 h-6 rounded cursor-pointer border-0"
                  />
                  <button onClick={() => onChange({ ...s, dataPointColor: '#ff4444' })} className="text-muted-foreground hover:text-foreground" title="Reset to default (#ff4444)">
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="p-2.5 rounded border bg-muted/20">
            <p className="text-xs text-muted-foreground">
              To show a trail, use <strong>Show Trail</strong> from the device's More menu. You can enable trails on multiple devices at once.
            </p>
          </div>

          {/* Advanced: Show for all devices (collapsed) */}
          <details className="text-xs" data-testid="trail-advanced">
            <summary className="text-muted-foreground cursor-pointer hover:text-foreground">Advanced options</summary>
            <div className="flex items-center justify-between mt-2 p-2 rounded border">
              <div>
                <Label className="text-xs">Show for all devices</Label>
                <p className="text-[10px] text-muted-foreground">Override: show trail for every device on the map</p>
              </div>
              <Switch checked={s.showAllDevices} onCheckedChange={(v) => onChange({ ...s, showAllDevices: v })} data-testid="trail-all-toggle" />
            </div>
          </details>
        </>
      )}
    </div>
  );
};

// Tab content: Animated Movement
const AnimationTab = ({ settings, onChange, allowed, snapAllowed }) => {
  if (!allowed) return <LockedMessage feature="Animated Movement" />;
  const s = { ...DEFAULT_SETTINGS.animation, ...settings };
  return (
    <div className="space-y-5" data-testid="animation-tab">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">Enable Animation</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Smooth interpolation between GPS position updates</p>
        </div>
        <Switch checked={s.enabled} onCheckedChange={(v) => onChange({ ...s, enabled: v })} data-testid="animation-toggle" />
      </div>
      {s.enabled && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Smooth Heading Rotation</Label>
              <p className="text-xs text-muted-foreground">Smoothly rotate marker arrows when heading changes</p>
            </div>
            <Switch checked={s.smoothRotation} onCheckedChange={(v) => onChange({ ...s, smoothRotation: v })} data-testid="animation-rotation-toggle" />
          </div>
          <div className="space-y-3 rounded-lg border bg-muted/20 p-3" data-testid="animation-road-snap-settings">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className={`text-sm ${!snapAllowed ? 'text-muted-foreground' : ''}`}>Use Snap to Road</Label>
                <p className="text-xs text-muted-foreground">Animate the marker along snapped road segments when available, with raw fallback if needed</p>
              </div>
              <Switch
                checked={snapAllowed && s.useRoadSnap !== false}
                onCheckedChange={(v) => onChange({ ...s, useRoadSnap: v })}
                disabled={!snapAllowed}
                data-testid="animation-road-snap-toggle"
              />
            </div>
            {!snapAllowed && <PremiumFeatureNote />}
          </div>
          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
            <p className="text-xs text-blue-700 dark:text-blue-400 font-medium mb-1">How it works</p>
            <p className="text-[11px] text-blue-600 dark:text-blue-300">
              Markers glide smoothly between GPS updates instead of jumping. The animation speed adapts to each device's upload interval — if a device reports every 30s, the marker moves steadily over ~27s, creating continuous movement. Heading rotates gradually to match direction changes from cornering, acceleration, and turns. Snapped-point styling stays under Trip Trail because the dots are trail-only.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

// Tab content: Live Stop Timer
const StopTimerTab = ({ settings, onChange, allowed }) => {
  if (!allowed) return <LockedMessage feature="Live Stop Timer" />;
  const s = { ...DEFAULT_SETTINGS.stopTimer, ...settings };
  return (
    <div className="space-y-5" data-testid="stop-timer-tab">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">Enable Engine Idle Timer</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Show idle duration on device markers</p>
        </div>
        <Switch checked={s.enabled} onCheckedChange={(v) => onChange({ ...s, enabled: v })} data-testid="stop-timer-toggle" />
      </div>
      {s.enabled && (
        <>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Require Ignition ON</Label>
              <p className="text-xs text-muted-foreground">Only show timer when engine is idling (ignition ON + stopped). When off, shows for any stopped vehicle.</p>
            </div>
            <Switch checked={s.requireIgnitionOn ?? true} onCheckedChange={(v) => onChange({ ...s, requireIgnitionOn: v })} data-testid="stop-timer-ign-toggle" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Color Thresholds</Label>
            <div className="space-y-2">
              {s.thresholds.map((t, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded border bg-muted/30">
                  <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: t.color }} />
                  <span className="text-xs flex-1">
                    {t.minMin} - {t.maxMin ?? '...'} min
                  </span>
                  <input
                    type="color"
                    value={t.color}
                    onChange={(e) => {
                      const newThresholds = [...s.thresholds];
                      newThresholds[i] = { ...t, color: e.target.value };
                      onChange({ ...s, thresholds: newThresholds });
                    }}
                    className="w-6 h-6 rounded cursor-pointer border-0"
                  />
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              Format on marker: "{s.requireIgnitionOn !== false ? 'Engine Idle' : 'Stopped'} 08:42"
            </p>
          </div>
        </>
      )}
    </div>
  );
};

// Tab content: Device Card Customization
const CardCustomTab = ({ settings, onChange, allowed }) => {
  if (!allowed) return <LockedMessage feature="Device Card Customization" />;
  const s = { ...DEFAULT_SETTINGS.cardCustom, ...settings };

  const colorOptions = [
    { key: 'offlineColor', darkKey: 'darkOfflineColor', label: 'Offline', desc: 'Vehicle offline' },
    { key: 'stoppedIgnOffColor', darkKey: 'darkStoppedIgnOffColor', label: 'Stopped (Ign OFF)', desc: 'Online, stopped, ignition OFF' },
    { key: 'stoppedIgnOnColor', darkKey: 'darkStoppedIgnOnColor', label: s.ignOnConditionEnabled ? 'Stopped (Ign ON) + Condition' : 'Stopped (Ign ON)', desc: 'Online, stopped, ignition ON' },
    { key: 'movingColor', darkKey: 'darkMovingColor', label: 'Moving', desc: 'Online, moving (>3 km/h)' },
  ];

  const borderColorOptions = [
    { key: 'borderOfflineColor', darkKey: 'darkBorderOfflineColor', label: 'Offline', desc: 'Vehicle offline' },
    { key: 'borderStoppedIgnOffColor', darkKey: 'darkBorderStoppedIgnOffColor', label: 'Stopped (Ign OFF)', desc: 'Online, stopped, ignition OFF' },
    { key: 'borderStoppedIgnOnColor', darkKey: 'darkBorderStoppedIgnOnColor', label: s.ignOnConditionEnabled ? 'Stopped (Ign ON) + Condition' : 'Stopped (Ign ON)', desc: 'Online, stopped, ignition ON' },
    { key: 'borderMovingColor', darkKey: 'darkBorderMovingColor', label: 'Moving', desc: 'Online, moving (>3 km/h)' },
  ];

  return (
    <div className="space-y-5" data-testid="card-custom-tab">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">Enable Card Customization</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Color-code device cards based on real-time status</p>
        </div>
        <Switch checked={s.enabled} onCheckedChange={(v) => onChange({ ...s, enabled: v })} data-testid="card-custom-toggle" />
      </div>
      {s.enabled && (
        <div className="space-y-4">
          {/* Mode Switches */}
          <div className="flex gap-3">
            <div className="flex-1 flex items-center justify-between p-2.5 rounded border">
              <div className="flex items-center gap-2">
                <Sun className="w-4 h-4 text-amber-500" />
                <Label className="text-xs font-medium">Light Mode</Label>
              </div>
              <Switch
                checked={s.lightModeEnabled ?? true}
                onCheckedChange={(v) => onChange({ ...s, lightModeEnabled: v })}
                data-testid="card-light-mode-toggle"
              />
            </div>
            <div className="flex-1 flex items-center justify-between p-2.5 rounded border">
              <div className="flex items-center gap-2">
                <Moon className="w-4 h-4 text-indigo-400" />
                <Label className="text-xs font-medium">Dark Mode</Label>
              </div>
              <Switch
                checked={s.darkModeEnabled ?? true}
                onCheckedChange={(v) => onChange({ ...s, darkModeEnabled: v })}
                data-testid="card-dark-mode-toggle"
              />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground -mt-2">
            Enable or disable card color customization independently for each theme mode.
          </p>

          {/* Ignition ON Condition */}
          <div className="p-3 rounded-lg border bg-muted/20 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium">Idle Time Trigger</Label>
              <Switch
                checked={s.ignOnConditionEnabled || false}
                onCheckedChange={(v) => onChange({ ...s, ignOnConditionEnabled: v })}
                data-testid="ign-on-condition-toggle"
              />
            </div>
            {s.ignOnConditionEnabled && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground whitespace-nowrap">Trigger after</Label>
                  <Input
                    type="number"
                    min={1}
                    max={60}
                    value={s.ignOnConditionMinutes ?? 5}
                    onChange={(e) => onChange({ ...s, ignOnConditionMinutes: Math.max(1, Math.min(60, Number(e.target.value) || 1)) })}
                    className="w-16 h-7 text-xs px-2 text-center"
                    data-testid="ign-on-condition-minutes"
                  />
                  <Label className="text-xs text-muted-foreground">minutes</Label>
                </div>
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  The card will only switch to the "Stopped (Ign ON)" colour once the Engine Idle Timer reaches the configured time above. Until then it stays on the "Stopped (Ign OFF)" colour.
                </p>
              </>
            )}
          </div>

          {/* Light Mode Colors */}
          {(s.lightModeEnabled ?? true) && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sun className="w-3.5 h-3.5 text-amber-500" />
                <Label className="text-xs text-muted-foreground">Light Mode Colors</Label>
              </div>
              <div className="space-y-2">
                {colorOptions.map((opt) => (
                  <div key={opt.key} className="flex items-center gap-3 p-2 rounded border">
                    <div className="w-7 h-7 rounded border flex-shrink-0" style={{ backgroundColor: s[opt.key] || 'transparent' }} />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-medium block">{opt.label}</span>
                      <span className="text-[10px] text-muted-foreground">{opt.desc}</span>
                    </div>
                    <input type="color" value={s[opt.key] || CARD_DEFAULTS[opt.key] || '#ffffff'} onChange={(e) => onChange({ ...s, [opt.key]: e.target.value })} className="w-6 h-6 rounded cursor-pointer border-0" />
                    <button onClick={() => onChange({ ...s, [opt.key]: CARD_DEFAULTS[opt.key] || '' })} className="text-muted-foreground hover:text-foreground" title="Reset to default">
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dark Mode Colors */}
          {(s.darkModeEnabled ?? true) && (
            <div className="pt-3 border-t">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Moon className="w-3.5 h-3.5 text-indigo-400" />
                  <Label className="text-xs font-medium">Dark Mode Colors</Label>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">{s.darkModeCustom ? 'Custom' : 'Defaults'}</span>
                  <Switch checked={s.darkModeCustom || false} onCheckedChange={(v) => onChange({ ...s, darkModeCustom: v })} data-testid="dark-mode-custom-toggle" />
                </div>
              </div>
              <div className="space-y-2">
                {colorOptions.map((opt) => {
                  const defaultDark = DARK_CARD_DEFAULTS[opt.darkKey] || '';
                  const darkColor = s.darkModeCustom ? (s[opt.darkKey] || defaultDark) : defaultDark;
                  return (
                    <div key={opt.darkKey} className="flex items-center gap-3 p-2 rounded border bg-slate-900/5 dark:bg-slate-800/50">
                      <div className="w-7 h-7 rounded border flex-shrink-0" style={{ backgroundColor: darkColor || 'transparent' }} />
                      <span className="text-xs flex-1">{opt.label}</span>
                      {s.darkModeCustom ? (
                        <>
                          <input type="color" value={darkColor || '#111111'} onChange={(e) => onChange({ ...s, [opt.darkKey]: e.target.value })} className="w-6 h-6 rounded cursor-pointer border-0" />
                          <button onClick={() => onChange({ ...s, [opt.darkKey]: DARK_CARD_DEFAULTS[opt.darkKey] || '' })} className="text-muted-foreground hover:text-foreground" title="Reset to default">
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        </>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">default</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Status Border Stripe ── */}
          <div className="pt-4 border-t space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Status Border Stripe</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Add a colored left border to indicate device status</p>
              </div>
              <Switch checked={s.borderEnabled || false} onCheckedChange={(v) => onChange({ ...s, borderEnabled: v })} data-testid="border-enabled-toggle" />
            </div>
            {s.borderEnabled && (
              <>
                {/* Border Thickness */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">Border Thickness: {s.borderThickness || 4}px</Label>
                  <Slider
                    value={[s.borderThickness || 4]}
                    onValueChange={([v]) => onChange({ ...s, borderThickness: v })}
                    min={2} max={12} step={1}
                    className="w-full"
                    data-testid="border-thickness-slider"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                    <span>2px</span><span>12px</span>
                  </div>
                </div>

                {/* Mode Switches for Border */}
                <div className="flex gap-3">
                  <div className="flex-1 flex items-center justify-between p-2.5 rounded border">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4 text-amber-500" />
                      <Label className="text-xs font-medium">Light</Label>
                    </div>
                    <Switch
                      checked={s.lightBorderEnabled ?? true}
                      onCheckedChange={(v) => onChange({ ...s, lightBorderEnabled: v })}
                      data-testid="border-light-mode-toggle"
                    />
                  </div>
                  <div className="flex-1 flex items-center justify-between p-2.5 rounded border">
                    <div className="flex items-center gap-2">
                      <Moon className="w-4 h-4 text-indigo-400" />
                      <Label className="text-xs font-medium">Dark</Label>
                    </div>
                    <Switch
                      checked={s.darkBorderEnabled ?? true}
                      onCheckedChange={(v) => onChange({ ...s, darkBorderEnabled: v })}
                      data-testid="border-dark-mode-toggle"
                    />
                  </div>
                </div>

                {/* Light Mode Border Colors */}
                {(s.lightBorderEnabled ?? true) && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Sun className="w-3.5 h-3.5 text-amber-500" />
                      <Label className="text-xs text-muted-foreground">Light Mode Border Colors</Label>
                    </div>
                    <div className="space-y-2">
                      {borderColorOptions.map((opt) => (
                        <div key={opt.key} className="flex items-center gap-3 p-2 rounded border">
                          <div className="w-1.5 h-7 rounded-full flex-shrink-0" style={{ backgroundColor: s[opt.key] || BORDER_DEFAULTS[opt.key] || '#9ca3af' }} />
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-medium block">{opt.label}</span>
                            <span className="text-[10px] text-muted-foreground">{opt.desc}</span>
                          </div>
                          <input type="color" value={s[opt.key] || BORDER_DEFAULTS[opt.key] || '#9ca3af'} onChange={(e) => onChange({ ...s, [opt.key]: e.target.value })} className="w-6 h-6 rounded cursor-pointer border-0" />
                          <button onClick={() => onChange({ ...s, [opt.key]: BORDER_DEFAULTS[opt.key] || '' })} className="text-muted-foreground hover:text-foreground" title="Reset to default">
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dark Mode Border Colors */}
                {(s.darkBorderEnabled ?? true) && (
                  <div className="pt-3 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Moon className="w-3.5 h-3.5 text-indigo-400" />
                        <Label className="text-xs font-medium">Dark Mode Border Colors</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground">{s.darkBorderCustom ? 'Custom' : 'Defaults'}</span>
                        <Switch checked={s.darkBorderCustom || false} onCheckedChange={(v) => onChange({ ...s, darkBorderCustom: v })} data-testid="dark-border-custom-toggle" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      {borderColorOptions.map((opt) => {
                        const defaultDark = DARK_BORDER_DEFAULTS[opt.darkKey] || '';
                        const darkColor = s.darkBorderCustom ? (s[opt.darkKey] || defaultDark) : defaultDark;
                        return (
                          <div key={opt.darkKey} className="flex items-center gap-3 p-2 rounded border bg-slate-900/5 dark:bg-slate-800/50">
                            <div className="w-1.5 h-7 rounded-full flex-shrink-0" style={{ backgroundColor: darkColor || '#6b7280' }} />
                            <span className="text-xs flex-1">{opt.label}</span>
                            {s.darkBorderCustom ? (
                              <>
                                <input type="color" value={darkColor || '#6b7280'} onChange={(e) => onChange({ ...s, [opt.darkKey]: e.target.value })} className="w-6 h-6 rounded cursor-pointer border-0" />
                                <button onClick={() => onChange({ ...s, [opt.darkKey]: DARK_BORDER_DEFAULTS[opt.darkKey] || '' })} className="text-muted-foreground hover:text-foreground" title="Reset to default">
                                  <RotateCcw className="w-3.5 h-3.5" />
                                </button>
                              </>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">default</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      )}
    </div>
  );
};

// Tab content: Advanced Road Speed Alert Configuration
const SpeedAlertTab = ({ settings, onChange, allowed, soundEnabled }) => {
  if (!allowed) return <LockedMessage feature="Advanced Road Speed Alert" />;
  const s = { ...DEFAULT_SETTINGS.speedAlert, ...settings };

  return (
    <div className="space-y-5" data-testid="speed-alert-tab">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">Enable Road Speed Alerts</Label>
          <p className="text-xs text-muted-foreground mt-0.5">Display road speed limits on the map and enable speed alert detection via Event Rules</p>
        </div>
        <Switch checked={s.enabled} onCheckedChange={(v) => onChange({ ...s, enabled: v })} data-testid="speed-alert-toggle" />
      </div>

      {s.enabled && (
        <>
          {/* Info note — speed alert configuration moved to Event Engine */}
          <div className="p-2.5 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 flex items-start gap-2">
            <Route className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700 dark:text-blue-400">
              <p className="font-medium mb-1">Speed alert configuration has moved to Event Rules</p>
              <p>Speed range tiers, buffer values, alert tones, and durations are now configured per Event Rule in <span className="font-medium">Analytics &rarr; Events &rarr; Event Rules</span>. This allows different alert settings per device group.</p>
              <p className="mt-1">Speed limit data: <span className="font-medium">Overpass (OSM)</span> &rarr; <span className="font-medium">OSRM Profile Estimate</span>.</p>
            </div>
          </div>

          {/* Speed Limit Display Toggle */}
          <div className="pt-3 border-t">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium">Show Speed Limit on Map</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Display the road speed limit badge on the map for devices with ignition ON. Telemetry panel always shows the speed limit.</p>
              </div>
              <Switch checked={s.showSpeedLimit ?? true} onCheckedChange={(v) => onChange({ ...s, showSpeedLimit: v })} data-testid="show-speed-limit-toggle" />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Premium feature note for snap-to-road
const PremiumFeatureNote = () => (
  <div className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800" data-testid="premium-snap-note">
    <Crown className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
    <p className="text-[11px] text-amber-700 dark:text-amber-400 leading-relaxed">
      <span className="font-semibold">Premium Feature</span> — Snap to Road is not enabled for your account. Contact your administrator to activate this feature.
    </p>
  </div>
);

// Locked feature message
const LockedMessage = ({ feature }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="locked-feature">
    <div className="w-14 h-14 rounded-full bg-muted/60 flex items-center justify-center mb-4">
      <Lock className="w-6 h-6 text-muted-foreground" />
    </div>
    <h3 className="text-sm font-semibold mb-1">{feature}</h3>
    <p className="text-xs text-muted-foreground max-w-[240px]">
      This feature is not enabled for your account. Contact your administrator to enable it in User Privileges.
    </p>
  </div>
);

export const MoreFeaturesModal = ({ open, onOpenChange, api, privileges, onSettingsChange, soundEnabled }) => {
  const [activeTab, setActiveTab] = useState('trail');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open || loaded) return;
    api.get('/map-features/settings')
      .then((res) => {
        if (res.data?.settings && Object.keys(res.data.settings).length > 0) {
          setSettings((prev) => {
            const merged = { ...prev };
            for (const key of Object.keys(prev)) {
              if (res.data.settings[key]) {
                merged[key] = { ...prev[key], ...res.data.settings[key] };
              }
            }
            return merged;
          });
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [open, api, loaded]);

  useEffect(() => {
    if (!open) setLoaded(false);
  }, [open]);

  const updateTabSettings = useCallback((tabId, value) => {
    setSettings((prev) => ({ ...prev, [tabId]: value }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/map-features/settings', { settings });
      onSettingsChange?.(settings);
      toast.success('Settings saved');
      onOpenChange(false);
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0" data-testid="more-features-modal">
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle className="text-lg font-semibold">Map Enhancement Features</DialogTitle>
          <p className="text-xs text-muted-foreground">Configure advanced tracking and display features</p>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 border-t mt-3">
          <div className="w-44 flex-shrink-0 border-r bg-muted/30 py-2">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const privKey = tab.id === 'trail' ? 'trip_direction_trail' : tab.id === 'animation' ? 'animated_movement' : tab.id === 'stopTimer' ? 'live_stop_timer' : tab.id === 'cardCustom' ? 'device_card_customization' : 'speed_alert_config';
              const isAllowed = privileges?.[privKey];
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#2563eb]/10 text-[#2563eb] font-medium border-r-2 border-[#2563eb]'
                      : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{tab.label}</span>
                  {!isAllowed && <Lock className="w-3 h-3 ml-auto text-muted-foreground/50" />}
                </button>
              );
            })}
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {activeTab === 'trail' && <TrailTab settings={settings.trail} onChange={(v) => updateTabSettings('trail', v)} allowed={privileges?.trip_direction_trail} snapAllowed={privileges?.snap_to_road} />}
            {activeTab === 'animation' && <AnimationTab settings={settings.animation} onChange={(v) => updateTabSettings('animation', v)} allowed={privileges?.animated_movement} snapAllowed={privileges?.snap_to_road} />}
            {activeTab === 'stopTimer' && <StopTimerTab settings={settings.stopTimer} onChange={(v) => updateTabSettings('stopTimer', v)} allowed={privileges?.live_stop_timer} />}
            {activeTab === 'cardCustom' && <CardCustomTab settings={settings.cardCustom} onChange={(v) => updateTabSettings('cardCustom', v)} allowed={privileges?.device_card_customization} />}
            {activeTab === 'speedAlert' && <SpeedAlertTab settings={settings.speedAlert} onChange={(v) => updateTabSettings('speedAlert', v)} allowed={privileges?.speed_alert_config} soundEnabled={soundEnabled} />}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-muted/20">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="more-features-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="more-features-save-btn">{saving ? 'Saving...' : 'Save Settings'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
